import React, { useState } from "react";

import ContentHeader from "components/Layout/ContentHeader";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import Tabs, { TabItem } from "components/UI/Tabs";
import "pages/AdminPage.scss";

interface Inquiry {
  id: string;
  subject: string;
  customer: string;
  category: "주문/배송" | "환불/취소" | "상품문의" | "기타";
  status: "pending" | "answered" | "closed";
  createdAt: string;
  answeredAt?: string;
}

const MOCK: Inquiry[] = [
  {
    id: "1",
    subject: "주문한 상품이 언제 도착하나요?",
    customer: "김민지",
    category: "주문/배송",
    status: "pending",
    createdAt: "2024-03-05 14:23",
  },
  {
    id: "2",
    subject: "상품 교환이 가능한가요?",
    customer: "이준영",
    category: "환불/취소",
    status: "answered",
    createdAt: "2024-03-04 09:11",
    answeredAt: "2024-03-04 16:30",
  },
  {
    id: "3",
    subject: "세트 구성 변경 가능 여부 문의",
    customer: "박소연",
    category: "상품문의",
    status: "pending",
    createdAt: "2024-03-04 22:47",
  },
  {
    id: "4",
    subject: "이중 결제가 된 것 같아요",
    customer: "최건우",
    category: "환불/취소",
    status: "answered",
    createdAt: "2024-03-03 11:05",
    answeredAt: "2024-03-03 13:20",
  },
  {
    id: "5",
    subject: "배송지 변경 요청드립니다",
    customer: "정유진",
    category: "주문/배송",
    status: "closed",
    createdAt: "2024-03-02 16:30",
    answeredAt: "2024-03-02 17:00",
  },
  {
    id: "6",
    subject: "포장 방식이 어떻게 되나요?",
    customer: "강현수",
    category: "상품문의",
    status: "pending",
    createdAt: "2024-03-05 08:15",
  },
];

const STATUS_BADGE: Record<string, "pending" | "active" | "draft"> = {
  pending: "pending",
  answered: "active",
  closed: "draft",
};
const STATUS_LABEL: Record<string, string> = {
  pending: "답변 대기",
  answered: "답변 완료",
  closed: "완료",
};

const TABS: TabItem[] = [
  { key: "all", label: "전체", count: 6 },
  { key: "pending", label: "답변 대기", count: 3 },
  { key: "answered", label: "답변 완료", count: 2 },
  { key: "closed", label: "완료", count: 1 },
];

const CustomerInquiry: React.FC = () => {
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [answer, setAnswer] = useState("");

  const filtered = MOCK.filter((i) => activeTab === "all" || i.status === activeTab);

  const columns: Column<Inquiry>[] = [
    { key: "subject", title: "제목", render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
    { key: "customer", title: "고객명", width: "90px" },
    {
      key: "category",
      title: "유형",
      width: "110px",
      render: (v) => <Badge variant="info">{v}</Badge>,
    },
    { key: "createdAt", title: "문의일", width: "140px" },
    { key: "answeredAt", title: "답변일", width: "140px", render: (v) => v || "-" },
    {
      key: "status",
      title: "상태",
      width: "100px",
      render: (v) => <Badge variant={STATUS_BADGE[v]}>{STATUS_LABEL[v]}</Badge>,
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader title="고객 문의" description="고객 문의를 조회하고 답변합니다." />

      <div className="admin-page__card">
        <Tabs
          items={TABS}
          activeKey={activeTab}
          onChange={(k) => {
            setActiveTab(k);
            setCurrentPage(1);
          }}
        />
        <div className="admin-page__filter">
          <Input placeholder="제목, 고객명 검색" style={{ width: 260 }} prefix="🔍" />
          <Select
            options={[
              { value: "", label: "유형 전체" },
              { value: "주문/배송", label: "주문/배송" },
              { value: "환불/취소", label: "환불/취소" },
              { value: "상품문의", label: "상품문의" },
              { value: "기타", label: "기타" },
            ]}
            style={{ width: 130 }}
          />
        </div>
        <Table
          columns={columns}
          data={filtered}
          rowKey="id"
          onRowClick={(row) => {
            setSelected(row);
            setAnswer("");
          }}
        />
        <Pagination current={currentPage} total={filtered.length} onChange={setCurrentPage} />
      </div>

      <Modal
        open={!!selected}
        title="문의 상세"
        onClose={() => setSelected(null)}
        width="560px"
        footer={
          <>
            <Button variant="secondary" onClick={() => setSelected(null)}>
              닫기
            </Button>
            {selected?.status === "pending" && (
              <Button variant="primary" onClick={() => setSelected(null)}>
                답변 등록
              </Button>
            )}
          </>
        }
      >
        {selected && (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div style={{ background: "#F8F8F8", borderRadius: 8, padding: 16 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <Badge variant="info">{selected.category}</Badge>
                <Badge variant={STATUS_BADGE[selected.status]}>{STATUS_LABEL[selected.status]}</Badge>
              </div>
              <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 8 }}>{selected.subject}</div>
              <div style={{ fontSize: 12, color: "#71717A" }}>
                작성자: {selected.customer} · {selected.createdAt}
              </div>
            </div>
            <div
              style={{
                background: "#FFFBEB",
                border: "1px solid #FDE68A",
                borderRadius: 8,
                padding: 16,
              }}
            >
              <div style={{ fontSize: 13, color: "#92400E", lineHeight: 1.6 }}>고객이 문의한 내용이 여기에 표시됩니다. 실제 서비스에서는 API를 통해 전체 문의 내용을 불러옵니다.</div>
            </div>
            {selected.status === "pending" && (
              <div>
                <label style={{ display: "block", fontWeight: 600, fontSize: 14, marginBottom: 8 }}>답변 작성</label>
                <textarea
                  rows={5}
                  placeholder="고객에게 보낼 답변을 작성해주세요."
                  value={answer}
                  onChange={(e) => setAnswer(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    border: "1px solid #E4E4E7",
                    borderRadius: 8,
                    fontSize: 13,
                    resize: "vertical",
                    outline: "none",
                    lineHeight: 1.6,
                  }}
                />
              </div>
            )}
            {selected.answeredAt && (
              <div
                style={{
                  background: "#F0FDF4",
                  border: "1px solid #BBF7D0",
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <div style={{ fontSize: 12, color: "#166534", fontWeight: 600, marginBottom: 6 }}>답변 완료 · {selected.answeredAt}</div>
                <div style={{ fontSize: 13, color: "#15803D", lineHeight: 1.6 }}>안녕하세요. 문의해주셔서 감사합니다. 담당 부서에 확인 후 빠르게 처리해드리겠습니다.</div>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CustomerInquiry;
